import { useState, useEffect, useContext } from 'react';
import { Grid, Card, CardContent, CardMedia, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, Chip, Stack, CircularProgress, Alert } from '@mui/material';
import { getMatchingAnnonces, acceptMatching, rejectMatching } from 'src/service/matchingApi';
import AuthContext from 'src/contexts/AuthContext';
import Page from '../components/Page';

export default function Matching() {
  const [annonces, setAnnonces] = useState([]);
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { customerId } = useContext(AuthContext);

  useEffect(() => {
    const fetchAnnonces = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMatchingAnnonces(customerId);
        setAnnonces(data);
      } catch (err) {
        setError('Erreur lors de la récupération des annonces');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchAnnonces();
    }
  }, [customerId]);

  const handleOpenDialog = (annonce) => {
    setSelectedAnnonce(annonce);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAnnonce(null);
  };

  const handleAccept = async (matchingId) => {
    try {
      await acceptMatching(selectedAnnonce.id, matchingId);
      // Mettre à jour la liste des annonces
      const updatedAnnonces = annonces.map(annonce => {
        if (annonce.id === selectedAnnonce.id) {
          return {
            ...annonce,
            matchings: annonce.matchings.filter(m => m.id !== matchingId)
          };
        }
        return annonce;
      });
      setAnnonces(updatedAnnonces);
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de l\'acceptation du matching:', error);
      setError('Erreur lors de l\'acceptation du matching');
    }
  };

  const handleReject = async (matchingId) => {
    try {
      await rejectMatching(selectedAnnonce.id, matchingId);
      // Mettre à jour la liste des annonces
      const updatedAnnonces = annonces.map(annonce => {
        if (annonce.id === selectedAnnonce.id) {
          return {
            ...annonce,
            matchings: annonce.matchings.filter(m => m.id !== matchingId)
          };
        }
        return annonce;
      });
      setAnnonces(updatedAnnonces);
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors du rejet du matching:', error);
      setError('Erreur lors du rejet du matching');
    }
  };

  if (loading) {
    return (
      <Page title="Matchings">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Matchings">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Page>
    );
  }

  return (
    <Page title="Matchings">
      {annonces.length === 0 ? (
        <Alert severity="info">
          Aucune annonce trouvée pour le moment.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {annonces.map((matching) => (
            <Grid item xs={12} sm={6} md={4} key={matching.annonceTrouve.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                  }
                }}
                onClick={() => handleOpenDialog(matching)}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={matching.annonceTrouve.imageUrl || matching.annonceTrouve.category.defaultImage}
                  alt={matching.annonceTrouve.details}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="div">
                    {matching.annonceTrouve.details}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    {matching.annonceTrouve.attributes?.map((attr, index) => (
                      <Chip 
                        key={index} 
                        label={`${attr.attribute}: ${attr.value}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Score: {matching.score}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ville: {matching.annonceTrouve.ville}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date: {new Date(matching.annonceTrouve.date).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {selectedAnnonce && (
          <>
            <DialogTitle>Détails du matching</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <CardMedia
                    component="img"
                    height="300"
                    image={selectedAnnonce.annonceTrouve.imageUrl || selectedAnnonce.annonceTrouve.category.defaultImage}
                    alt={selectedAnnonce.annonceTrouve.details}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Détails de l'annonce trouvée
                  </Typography>
                  <Stack spacing={2}>
                    {selectedAnnonce.annonceTrouve.attributes?.map((attr, index) => (
                      <Box key={index}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {attr.attribute}
                        </Typography>
                        <Typography variant="body1">
                          {attr.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  
                  <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                    Annonce perdue correspondante
                  </Typography>
                  <Card sx={{ mb: 2, p: 2 }}>
                    <Typography variant="subtitle1">
                      {selectedAnnonce.annoncePerdu.details}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ville: {selectedAnnonce.annoncePerdu.ville}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date: {new Date(selectedAnnonce.annoncePerdu.date).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        size="small"
                        onClick={() => handleAccept(selectedAnnonce.annonceTrouve.id, selectedAnnonce.annoncePerdu.id)}
                      >
                        Accepter
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => handleReject(selectedAnnonce.annonceTrouve.id, selectedAnnonce.annoncePerdu.id)}
                      >
                        Rejeter
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Fermer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Page>
  );
} 